import { useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import PhoneNumberInput from '../../components/PhoneNumberInput';

type ChurchProfileForm = {
  name: string;
  denomination: string;
  pastorName: string;
  address: string;
  phone: string;
  worshipTime: string;
  intro: string;
};

const EMPTY_PROFILE: ChurchProfileForm = {
  name: '',
  denomination: '',
  pastorName: '',
  address: '',
  phone: '',
  worshipTime: '',
  intro: '',
};

const DENOMINATION_OPTIONS = [
  '구세군대한본영',
  '기독교대한감리회',
  '기독교대한성결교회',
  '기독교대한하나님의성회',
  '기독교한국침례회',
  '대한기독교나사렛성결회',
  '대한예수교장로회고신',
  '대한예수교장로회백석',
  '대한예수교장로회통합',
  '대한예수교장로회합동',
  '대한예수교장로회합신',
  '예수교대한성결교회',
  '한국기독교장로회',
  '독립교단(KAICAM)',
];

export default function ManageChurchProfile({ authChurch }: { authChurch: string }) {
  const [form, setForm] = useState<ChurchProfileForm>(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${MainURL}/church-profile`, { params: { authChurch } })
      .then((res) => {
        const data = res.data?.data;
        setForm(
          data
            ? {
                name: data.name ?? '',
                denomination: data.denomination ?? '',
                pastorName: data.pastorName ?? '',
                address: data.address ?? '',
                phone: data.phone ?? '',
                worshipTime: data.worshipTime ?? '',
                intro: data.intro ?? '',
              }
            : EMPTY_PROFILE,
        );
      })
      .catch((err) => console.error('GET /church-profile', err))
      .finally(() => setLoading(false));
  }, [authChurch]);

  const save = () => {
    setSaving(true);
    setMessage(null);
    axios
      .put(`${MainURL}/church-profile`, { authChurch, ...form })
      .then(() => setMessage('저장되었습니다.'))
      .catch((err) => {
        console.error('PUT /church-profile', err);
        setMessage('저장에 실패했습니다.');
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">교회정보 관리</h2>
          <p className="manage-panel-desc">앱의 '나의 교회' 화면에 표시되는 교회 기본 정보를 편집합니다.</p>
        </div>
      </div>

      <section className="manage-card-block">
        <div className="manage-block-head">
          <h3 className="manage-block-title">기본 정보</h3>
        </div>
        <div className="manage-block-body">
          {loading ? (
            <div className="manage-state">불러오는 중입니다...</div>
          ) : (
            <div className="manage-form manage-form--grid">
              <label className="manage-field">
                <span>교회명</span>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="예: 은혜인교회"
                />
              </label>
              <label className="manage-field">
                <span>교단</span>
                <select
                  value={form.denomination}
                  onChange={(e) => setForm({ ...form, denomination: e.target.value })}
                >
                  <option value="">선택 안 함</option>
                  {DENOMINATION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label className="manage-field">
                <span>담임목사</span>
                <input
                  value={form.pastorName}
                  onChange={(e) => setForm({ ...form, pastorName: e.target.value })}
                  placeholder="예: 홍길동 목사"
                />
              </label>
              <label className="manage-field manage-field--full">
                <span>주소</span>
                <input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="예: 서울특별시 ○○구 ○○로 123"
                />
              </label>
              <label className="manage-field">
                <span>전화</span>
                <PhoneNumberInput
                  value={form.phone}
                  onChange={(phone) => setForm({ ...form, phone })}
                  lengths={[4, 4, 4]}
                />
              </label>
              <label className="manage-field">
                <span>예배시간</span>
                <input
                  value={form.worshipTime}
                  onChange={(e) => setForm({ ...form, worshipTime: e.target.value })}
                  placeholder="예: 주일 11:00, 수요 19:30"
                />
              </label>
              <label className="manage-field manage-field--full">
                <span>교회소개</span>
                <textarea
                  value={form.intro}
                  onChange={(e) => setForm({ ...form, intro: e.target.value })}
                  placeholder="교회 소개 문구를 입력하세요."
                  rows={4}
                />
              </label>
            </div>
          )}
        </div>
      </section>

      <div className="manage-form-actions">
        {message && <span className="manage-inline-message">{message}</span>}
        <button type="button" className="manage-btn manage-btn--primary" onClick={save} disabled={saving || loading}>
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>
    </div>
  );
}
